import React, { useEffect, useState } from "react"
import types from "prop-types"
import * as API from "services/api"
import { isEmpty } from "lodash-es"
import {
  dayjsOrgTz,
  DAYS, MONTHS, WEEKS,
} from "utils/dateHelpers"
import { formSlugOptionShape, newTaskReportExportTaskReportShape } from "utils/propTypeShapes"
import { errorToast, successToast } from "components/shared/toast"
import { ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH, redirectTo } from "utils/routeHelpers"
import {
  CREATE_SCHEDULED_REPORT_EXPORT_ERROR_MESSAGE,
  MAX_DAYS,
  MAX_WEEKS,
  MAX_MONTHS,
  TASK_CLOSED_DATE_FILTER_HEADER,
  TASK_UPDATED_DATE_FILTER_HEADER,
} from "utils/scheduledReportExportHelpers"
import ScheduledTaskReportExportContext from "../ScheduledTaskReportExportContext"

// Values correspond to the backend's ScheduledReportExport::TaskFilter timestamp filters.
const ON_UPDATED_AT_FILTER = "on_updated_at"
const ON_CLOSED_AT_FILTER = "on_closed_at"

export const DATE_FILTER_TYPE_OPTIONS = [TASK_UPDATED_DATE_FILTER_HEADER, TASK_CLOSED_DATE_FILTER_HEADER]

const timestampFilterForDateFilterType = (dateFilterType) => (
  dateFilterType === TASK_CLOSED_DATE_FILTER_HEADER ? ON_CLOSED_AT_FILTER : ON_UPDATED_AT_FILTER
)

const dateFilterTypeForTimestampFilter = (timestampFilter) => (
  timestampFilter === ON_CLOSED_AT_FILTER ? TASK_CLOSED_DATE_FILTER_HEADER : TASK_UPDATED_DATE_FILTER_HEADER
)

const ScheduledTaskReportExportContextProvider = ({
  children,
  scheduledTaskReportExport: propScheduledTaskReportExport = null,
  submittableForms,
  taskReport: propTaskReport,
}) => {
  const [taskReport] = useState(propTaskReport)
  const { id: taskReportId, name: taskReportName } = taskReport

  const updatingExistingRecord = !!propScheduledTaskReportExport

  const [scheduledReportExportName, setScheduledReportExportName] = useState("")
  const [frequency, setReportFrequency] = useState("")
  const [recurrenceDayOfWeek, setRecurrenceDayOfWeek] = useState("")
  const [recurrenceDayOfMonth, setRecurrenceDayOfMonth] = useState(null)
  const [startDate, setStartDate] = useState(null)
  const [activeStatus, setActiveStatus] = useState(false)

  const [dateFilterType, setDateFilterType] = useState("")
  const [taskPreviousRangeLength, setTaskPreviousRangeLength] = useState("")
  const [taskPreviousRangeUnit, setTaskPreviousRangeUnit] = useState("")

  const [includeOpenTasks, setIncludeOpenTasks] = useState(true)
  const [includeClosedTasks, setIncludeClosedTasks] = useState(true)

  const submittableFormSlugs = submittableForms.map(({ slug }) => slug)
  const [includeAllForms, setIncludeAllForms] = useState(true)
  const [includedFormSlugs, setIncludedFormSlugs] = useState([])

  const maxBasedOnUnit = () => {
    if (taskPreviousRangeUnit === DAYS) {
      return MAX_DAYS
    } if (taskPreviousRangeUnit === WEEKS) {
      return MAX_WEEKS
    } if (taskPreviousRangeUnit === MONTHS) {
      return MAX_MONTHS
    }
    return 10
  }

  const lengthOptions = Array.from({ length: maxBasedOnUnit() }, (_, index) => index + 1)

  // A closed date window can only ever match closed tasks, so the statuses stop applying when it is
  // chosen.  The backend normalizes them regardless; clearing the length keeps the range step from
  // carrying a value chosen for a different window.
  const setDateFilterTypeAndClearValues = (selectedDateFilterType) => {
    setTaskPreviousRangeLength("")
    setDateFilterType(selectedDateFilterType)
  }

  const toggleOpenTasks = () => setIncludeOpenTasks((currentlyIncluded) => !currentlyIncluded)
  const toggleClosedTasks = () => setIncludeClosedTasks((currentlyIncluded) => !currentlyIncluded)

  // Choosing every form clears the selection, so an empty stored list is what means "all current and
  // future forms" -- the same way an empty survey configuration list does.
  const chooseAllForms = () => {
    setIncludeAllForms(true)
    setIncludedFormSlugs([])
  }

  const chooseSpecificForms = () => setIncludeAllForms(false)

  const selectAllFormSlugs = () => setIncludedFormSlugs(submittableFormSlugs)
  const deselectAllFormSlugs = () => setIncludedFormSlugs([])
  const isFormSlugSelected = (slug) => includedFormSlugs.includes(slug)

  const toggleIncludedFormSlug = (slug) => {
    setIncludedFormSlugs((currentlyIncluded) => (
      currentlyIncluded.includes(slug)
        ? currentlyIncluded.filter((includedSlug) => includedSlug !== slug)
        : [...currentlyIncluded, slug]
    ))
  }

  const filteringOnClosedDate = dateFilterType === TASK_CLOSED_DATE_FILTER_HEADER
  const statusesApply = dateFilterType === TASK_UPDATED_DATE_FILTER_HEADER
  const noTaskStatusIncluded = statusesApply && !includeOpenTasks && !includeClosedTasks
  const noSubmittableFormIncluded = !includeAllForms && isEmpty(includedFormSlugs)

  const isFormValid = !!startDate
    && !!dateFilterType
    && !!taskPreviousRangeLength
    && !noTaskStatusIncluded
    && !noSubmittableFormIncluded

  const saveScheduledReportExport = async () => {
    const payload = {
      activeStatus,
      frequency,
      includeClosedTasks,
      includedFormSlugs,
      includeOpenTasks,
      recurrenceDayOfMonth,
      recurrenceDayOfWeek,
      reportId: taskReportId,
      scheduledReportExportName,
      startDate,
      taskPreviousRangeLength,
      taskPreviousRangeUnit,
      taskTimestampFilter: timestampFilterForDateFilterType(dateFilterType),
    }

    let response
    if (updatingExistingRecord) {
      const { id: scheduledReportExportId } = propScheduledTaskReportExport

      response = await API.updateScheduledTaskReportExport({ ...payload, scheduledReportExportId })
    } else {
      response = await API.createScheduledTaskReportExport(payload)
    }

    if (response.ok) {
      successToast(`Scheduled report export ${scheduledReportExportName} has been saved.`)

      setTimeout(() => { redirectTo(ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH) }, 1000)
    } else {
      errorToast(CREATE_SCHEDULED_REPORT_EXPORT_ERROR_MESSAGE)
    }
  }

  const cancelForm = () => { redirectTo(ACTIVITY_HUB_FORM_SUBMISSION_REPORTS_PATH) }

  useEffect(() => {
    if (!updatingExistingRecord) return

    const {
      includeClosedTasks: propIncludeClosedTasks,
      includedFormSlugs: propIncludedFormSlugs,
      includeOpenTasks: propIncludeOpenTasks,
      name,
      scheduledReportExportSchedule,
      taskPreviousRangeLength: propTaskPreviousRangeLength,
      taskPreviousRangeUnit: propTaskPreviousRangeUnit,
      taskTimestampFilter,
    } = propScheduledTaskReportExport

    const {
      active, frequency: scheduleFrequency, recurrenceDayOfMonth: dayOfMonth,
      recurrenceDayOfWeek: dayOfWeek, startDate: scheduleStartDate,
    } = scheduledReportExportSchedule

    setScheduledReportExportName(name)
    setActiveStatus(active)
    setReportFrequency(scheduleFrequency)
    setRecurrenceDayOfMonth(dayOfMonth)
    setRecurrenceDayOfWeek(dayOfWeek || "")
    setStartDate(dayjsOrgTz(scheduleStartDate))

    setDateFilterType(dateFilterTypeForTimestampFilter(taskTimestampFilter))
    setTaskPreviousRangeLength(propTaskPreviousRangeLength)
    setTaskPreviousRangeUnit(propTaskPreviousRangeUnit)

    setIncludeOpenTasks(propIncludeOpenTasks)
    setIncludeClosedTasks(propIncludeClosedTasks)

    // An empty stored list is the "all current and future forms" case rather than a selection of
    // nothing, so the radio is derived from whether anything was stored.
    setIncludeAllForms(isEmpty(propIncludedFormSlugs))
    setIncludedFormSlugs(propIncludedFormSlugs || [])
  // Deps are intentionally omitted -- this is a one-time hydration effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const providedValue = {
    activeStatus,
    cancelForm,
    chooseAllForms,
    chooseSpecificForms,
    dateFilterType,
    deselectAllFormSlugs,
    filteringOnClosedDate,
    frequency,
    includeAllForms,
    includeClosedTasks,
    includedFormSlugs,
    includeOpenTasks,
    isFormSlugSelected,
    isFormValid,
    lengthOptions,
    noSubmittableFormIncluded,
    noTaskStatusIncluded,
    recurrenceDayOfMonth,
    recurrenceDayOfWeek,
    saveScheduledReportExport,
    scheduledReportExportName,
    selectAllFormSlugs,
    setActiveStatus,
    setDateFilterTypeAndClearValues,
    setRecurrenceDayOfMonth,
    setRecurrenceDayOfWeek,
    setReportFrequency,
    setScheduledReportExportName,
    setStartDate,
    setTaskPreviousRangeLength,
    setTaskPreviousRangeUnit,
    startDate,
    statusesApply,
    submittableForms,
    taskPreviousRangeLength,
    taskPreviousRangeUnit,
    taskReportId,
    taskReportName,
    toggleClosedTasks,
    toggleIncludedFormSlug,
    toggleOpenTasks,
    updatingExistingRecord,
  }

  return (
    <ScheduledTaskReportExportContext.Provider value={providedValue}>
      {children}
    </ScheduledTaskReportExportContext.Provider>
  )
}

ScheduledTaskReportExportContextProvider.propTypes = {
  children: types.node.isRequired,
  scheduledTaskReportExport: types.object, // eslint-disable-line react/forbid-prop-types
  submittableForms: types.arrayOf(formSlugOptionShape).isRequired,
  taskReport: newTaskReportExportTaskReportShape.isRequired,
}

export default ScheduledTaskReportExportContextProvider
